import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import ScrollToTop from "@/components/ScrollToTop";
import PageTransition from "@/components/PageTransition";
import HomePage from "@/pages/HomePage";
import {
  loadAboutPage,
  loadServicesPage,
  loadProjectsPage,
  loadProjectDetailPage,
  loadContactPage,
  loadDreamsPage,
  loadPrivacyPolicyPage,
  loadTermsOfUsePage,
  warmRouteChunks,
  warmHeroImages,
} from "@/lib/pageLoaders";

// Admin
import { AdminAuthProvider } from "@/admin/AdminAuthContext";
import ProtectedRoute from "@/admin/ProtectedRoute";
import AdminLayout from "@/admin/AdminLayout";
import AdminLoginPage from "@/admin/pages/LoginPage";
import AdminDashboardPage from "@/admin/pages/DashboardPage";
import AdminProjectsPage from "@/admin/pages/ProjectsPage";
import AdminProjectFormPage from "@/admin/pages/ProjectFormPage";
import AdminServicesPage from "@/admin/pages/ServicesPage";
import AdminMessagesPage from "@/admin/pages/MessagesPage";
import AdminDreamRequestsPage from "@/admin/pages/DreamRequestsPage";
import AdminCompanyInfoPage from "@/admin/pages/CompanyInfoPage";

// Public lazy pages
const AboutPage = lazy(loadAboutPage);
const ServicesPage = lazy(loadServicesPage);
const ProjectsPage = lazy(loadProjectsPage);
const ProjectDetailPage = lazy(loadProjectDetailPage);
const ContactPage = lazy(loadContactPage);
const DreamsPage = lazy(loadDreamsPage);
const PrivacyPolicyPage = lazy(loadPrivacyPolicyPage);
const TermsOfUsePage = lazy(loadTermsOfUsePage);

const PageFallback = () => (
  <div style={{ minHeight: "100vh", background: "#050505" }} />
);

// ── Animated public routes ────────────────────────────────────────────────────
const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="sync">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route
          path="/hakkimizda"
          element={<PageTransition><Suspense fallback={<PageFallback />}><AboutPage /></Suspense></PageTransition>}
        />
        <Route
          path="/hizmetler"
          element={<PageTransition><Suspense fallback={<PageFallback />}><ServicesPage /></Suspense></PageTransition>}
        />
        <Route
          path="/projeler"
          element={<PageTransition><Suspense fallback={<PageFallback />}><ProjectsPage /></Suspense></PageTransition>}
        />
        <Route
          path="/projeler/:projectId"
          element={<PageTransition><Suspense fallback={<PageFallback />}><ProjectDetailPage /></Suspense></PageTransition>}
        />
        <Route
          path="/iletisim"
          element={<PageTransition><Suspense fallback={<PageFallback />}><ContactPage /></Suspense></PageTransition>}
        />
        <Route
          path="/dreams"
          element={<PageTransition><Suspense fallback={<PageFallback />}><DreamsPage /></Suspense></PageTransition>}
        />
        <Route
          path="/gizlilik-politikasi"
          element={<PageTransition><Suspense fallback={<PageFallback />}><PrivacyPolicyPage /></Suspense></PageTransition>}
        />
        <Route
          path="/kullanim-sartlari"
          element={<PageTransition><Suspense fallback={<PageFallback />}><TermsOfUsePage /></Suspense></PageTransition>}
        />
      </Routes>
    </AnimatePresence>
  );
};

// ── Root router ───────────────────────────────────────────────────────────────
function AppInner() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <>
        <Toaster position="top-right" theme="dark" richColors closeButton />
        <AdminAuthProvider>
          <Routes>
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboardPage />} />
              <Route path="projects" element={<AdminProjectsPage />} />
              <Route path="projects/new" element={<AdminProjectFormPage />} />
              <Route path="projects/:id/edit" element={<AdminProjectFormPage />} />
              <Route path="services" element={<AdminServicesPage />} />
              <Route path="messages" element={<AdminMessagesPage />} />
              <Route path="dream-requests" element={<AdminDreamRequestsPage />} />
              <Route path="company-info" element={<AdminCompanyInfoPage />} />
            </Route>
          </Routes>
        </AdminAuthProvider>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Header />
      <AnimatedRoutes />
    </>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  useEffect(() => {
    const id = window.setTimeout(() => {
      warmRouteChunks();
      warmHeroImages();
    }, 300);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </div>
  );
}

export default App;
